import React, { useState, useRef, useEffect } from "react";
import { Badge } from "react-bootstrap";
import { FiChevronDown, FiX } from "react-icons/fi";
import "./PlanoBadgeSelector.css";

/**
 * Componente visual minimalista para seleção de planos com badges de alerta
 * Mostra desvios (vermelho) e alertas estatísticos (amarelo)
 * Com suporte a busca/filtro por nome
 */
const PlanoBadgeSelector = ({
  selectedPlanoId,
  setSelectedPlanoId,
  planoOptions = [], // Array com { value, label, totalRegistrosForaGlobal, itensAlertaEstatistica }
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filtrar opções baseado no termo de busca
  const filteredOptions = searchTerm
    ? planoOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : planoOptions;

  const selectedOption = planoOptions.find(
    (o) => String(o.value) === String(selectedPlanoId)
  );

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Focar no input quando dropdown abre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectPlano = (value) => {
    setSelectedPlanoId(value);
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <div
      className="plano-badge-selector-container"
      ref={containerRef}
    >
      {/* Botão de seleção */}
      <button
        className="plano-badge-selector-button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="plano-badge-selector-content">
          <span className="plano-badge-selector-label">
            {selectedOption ? selectedOption.label : "Selecione um plano"}
          </span>
          <div className="plano-badge-selector-badges">
            {selectedOption?.totalRegistrosForaGlobal > 0 && (
              <Badge
                bg="danger"
                title={`${selectedOption.totalRegistrosForaGlobal} registros fora de especificação`}
                className="plano-badge-item"
              >
                {selectedOption.totalRegistrosForaGlobal} Fora
              </Badge>
            )}
            {selectedOption?.itensAlertaEstatistica > 0 && (
              <Badge
                bg="warning"
                text="dark"
                title={`${selectedOption.itensAlertaEstatistica} controles com alertas`}
                className="plano-badge-item"
              >
                {selectedOption.itensAlertaEstatistica} Alertas
              </Badge>
            )}
          </div>
        </div>
        <FiChevronDown
          className={`plano-badge-selector-icon ${
            isOpen ? "plano-badge-selector-icon--open" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="plano-badge-selector-dropdown">
          {/* Campo de busca */}
          <div className="plano-badge-selector-search">
            <input
              ref={inputRef}
              type="text"
              placeholder="Digite para filtrar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="plano-badge-selector-search-input"
              aria-label="Buscar plano"
            />
            {searchTerm && (
              <button
                className="plano-badge-selector-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Limpar busca"
                type="button"
              >
                <FiX size={16} />
              </button>
            )}
          </div>

          <div className="plano-badge-selector-list">
            {filteredOptions.length === 0 ? (
              <div className="plano-badge-selector-empty">
                {searchTerm
                  ? "Nenhum plano encontrado"
                  : "Nenhum plano disponível"}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  className={`plano-badge-selector-item ${
                    String(option.value) === String(selectedPlanoId)
                      ? "plano-badge-selector-item--selected"
                      : ""
                  }`}
                  onClick={() => handleSelectPlano(option.value)}
                  type="button"
                >
                  <span className="plano-badge-selector-item-label">
                    {option.label}
                  </span>
                  <div className="plano-badge-selector-item-badges">
                    {option.totalRegistrosForaGlobal > 0 && (
                      <Badge
                        bg="danger"
                        title={`${option.totalRegistrosForaGlobal} registros fora`}
                        className="plano-badge-item"
                      >
                        {option.totalRegistrosForaGlobal}
                      </Badge>
                    )}
                    {option.itensAlertaEstatistica > 0 && (
                      <Badge
                        bg="warning"
                        text="dark"
                        title={`${option.itensAlertaEstatistica} alertas`}
                        className="plano-badge-item plano-badge-item--small"
                      >
                        {option.itensAlertaEstatistica}
                      </Badge>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoBadgeSelector;
